/**
 * Supplier Service - Frontend API service for supplier management
 */

interface Supplier {
  id: string;
  name: string;
  company_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  created_at?: string;
  updated_at?: string;
}

interface CreateSupplierRequest {
  name: string;
  company_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
}

interface SupplierFilters {
  search?: string;
  email?: string;
  limit?: number;
  offset?: number;
}

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';

export class SupplierService {
  private baseUrl = API_BASE_URL;

  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get all suppliers with optional filtering
   */
  async getSuppliers(filters: SupplierFilters = {}): Promise<ApiResponse<Supplier[]>> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.email) params.append('email', filters.email);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${this.baseUrl}/suppliers?${params}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Fehler beim Laden der Lieferanten' };
      }

      return { 
        success: true, 
        data: result.data || [],
        message: 'Lieferanten erfolgreich geladen' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Get supplier by ID
   */
  async getSupplierById(supplierId: string): Promise<ApiResponse<Supplier>> {
    try {
      const response = await fetch(`${this.baseUrl}/suppliers/${supplierId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Lieferant nicht gefunden' };
      }

      return { 
        success: true, 
        data: result.data,
        message: 'Lieferant erfolgreich geladen' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Create new supplier
   */
  async createSupplier(supplierData: CreateSupplierRequest): Promise<ApiResponse<Supplier>> {
    try {
      const response = await fetch(`${this.baseUrl}/suppliers`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(supplierData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Fehler beim Erstellen des Lieferanten' };
      }

      return { 
        success: true, 
        data: result.data,
        message: 'Lieferant erfolgreich erstellt' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Update supplier
   */
  async updateSupplier(supplierId: string, updates: Partial<CreateSupplierRequest>): Promise<ApiResponse<Supplier>> {
    try {
      const response = await fetch(`${this.baseUrl}/suppliers/${supplierId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updates)
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Fehler beim Aktualisieren des Lieferanten' };
      }

      return { 
        success: true, 
        data: result.data,
        message: 'Lieferant erfolgreich aktualisiert' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Delete supplier
   */
  async deleteSupplier(supplierId: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Fehler beim Löschen des Lieferanten' };
      }

      return { 
        success: true,
        message: 'Lieferant erfolgreich gelöscht' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Search suppliers by email
   */
  async findSupplierByEmail(email: string): Promise<ApiResponse<Supplier | null>> {
    try {
      const response = await this.getSuppliers({ email, limit: 1 });
      
      if (response.success && response.data && response.data.length > 0) {
        return {
          success: true,
          data: response.data[0],
          message: 'Lieferant gefunden'
        };
      }

      return {
        success: true,
        data: null,
        message: 'Kein Lieferant mit dieser E-Mail gefunden'
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }
}

// Export singleton instance
export const supplierService = new SupplierService();
export type { Supplier, CreateSupplierRequest, SupplierFilters };
